import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, User as UserIcon, Mail, Shield, CheckCircle, XCircle, Calendar, Clock, Activity, Key, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const relative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  return `${diffMo}mo ago`;
};

const UserDetailDrawer = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/users/${userId}/detail`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load user details');
      }
      setLoading(false);
    };
    if (userId) fetchDetail();
  }, [userId]);

  if (!userId) return null;

  const u = data?.user;
  const sessions = data?.sessions || [];
  const stats = data?.stats || {};

  return (
    <div
      data-testid="user-detail-drawer"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      {/* backdrop */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />

      {/* drawer */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 520,
          height: '100%',
          background: '#120a25',
          borderLeft: '1px solid rgba(139,92,246,0.25)',
          overflowY: 'auto',
          boxShadow: '-20px 0 40px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'rgba(18, 10, 37, 0.95)',
          backdropFilter: 'blur(10px)',
          padding: '18px 22px',
          borderBottom: '1px solid rgba(139,92,246,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 5,
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#e0dff0' }}>User Details</h2>
          <button
            data-testid="close-user-drawer"
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8, border: 'none',
              background: 'rgba(139,92,246,0.15)', color: '#b0adc0', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: 'center', color: '#8885a0' }}>
            <Loader2 size={20} className="animate-spin" style={{ marginBottom: 10 }} />
            <div>Loading user details…</div>
          </div>
        )}
        {error && (
          <div style={{ padding: 22, color: '#fca5a5' }}>{error}</div>
        )}

        {u && (
          <div style={{ padding: 22 }}>
            {/* Profile */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22,
              padding: 16,
              background: 'rgba(139,92,246,0.08)',
              border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: 14,
            }}>
              {u.picture
                ? <img src={u.picture} alt="" style={{ width: 64, height: 64, borderRadius: '50%', border: '2px solid rgba(139,92,246,0.4)' }} />
                : <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserIcon size={28} color="#a855f7" />
                  </div>}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#e0dff0', marginBottom: 2 }}>{u.name || 'Unnamed'}</div>
                <div style={{ fontSize: 13, color: '#8885a0', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Mail size={12} /> {u.email}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <Badge color={u.status === 'active' ? '#22c55e' : '#ef4444'}>
                    {u.status || 'active'}
                  </Badge>
                  <Badge color={u.is_admin ? '#a855f7' : '#8885a0'}>
                    {u.is_admin ? 'Admin' : 'Member'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 22 }}>
              <StatCard icon={Calendar} label="Joined" value={fmt(u.created_at)} sub={relative(u.created_at)} color="#00d4ff" />
              <StatCard icon={Clock} label="Last Login" value={fmt(u.last_login_at)} sub={relative(u.last_login_at)} color="#ffd700" />
              <StatCard icon={Activity} label="Total Logins" value={u.login_count ?? 0} color="#22c55e" />
              <StatCard icon={Key} label="Active Sessions" value={stats.active_sessions ?? 0} sub={`${stats.total_sessions_ever ?? 0} total ever`} color="#a855f7" />
            </div>

            {/* Raw info */}
            <Section title="Account Info">
              <Row k="User ID" v={u.user_id} mono />
              <Row k="Email" v={u.email} />
              <Row k="Role" v={u.is_admin ? 'Administrator' : 'Standard Member'} />
              <Row k="Status" v={u.status || 'active'} />
              <Row k="Joined At" v={fmt(u.created_at)} />
              <Row k="Last Login" v={fmt(u.last_login_at)} />
              <Row k="Login Count" v={u.login_count ?? 0} />
              {u.picture && <Row k="Profile Picture" v={<a href={u.picture} target="_blank" rel="noreferrer" style={{ color: '#00d4ff' }}>View</a>} />}
            </Section>

            {/* Sessions */}
            <Section title={`Sessions (${sessions.length})`}>
              {sessions.length === 0 && <div style={{ fontSize: 13, color: '#8885a0', padding: 10 }}>No sessions recorded.</div>}
              {sessions.map((s, i) => (
                <div
                  key={i}
                  data-testid={`session-row-${i}`}
                  style={{
                    padding: 12,
                    marginBottom: 8,
                    borderRadius: 10,
                    background: s.is_active ? 'rgba(34,197,94,0.06)' : 'rgba(136,133,160,0.05)',
                    border: `1px solid ${s.is_active ? 'rgba(34,197,94,0.2)' : 'rgba(136,133,160,0.15)'}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    {s.is_active
                      ? <Badge color="#22c55e"><CheckCircle size={10} style={{ marginRight: 4 }} />Active</Badge>
                      : <Badge color="#8885a0"><XCircle size={10} style={{ marginRight: 4 }} />Expired</Badge>}
                    <span style={{ fontSize: 11, color: '#8885a0', fontFamily: 'monospace' }}>{s.session_token}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#b0adc0' }}>
                    Created {fmt(s.created_at)} · Expires {fmt(s.expires_at)}
                  </div>
                </div>
              ))}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== helpers ==========
const Badge = ({ color, children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: `${color}22`, color,
  }}>{children}</span>
);

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div style={{
    padding: 14,
    borderRadius: 12,
    background: `${color}08`,
    border: `1px solid ${color}30`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
      <Icon size={14} color={color} />
      <span style={{ fontSize: 11, color: '#8885a0', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
    </div>
    <div style={{ fontSize: 15, fontWeight: 600, color: '#e0dff0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#8885a0', marginTop: 2 }}>{sub}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <h3 style={{ fontSize: 12, color: '#8885a0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, fontWeight: 600 }}>{title}</h3>
    <div style={{
      background: 'rgba(139,92,246,0.05)',
      border: '1px solid rgba(139,92,246,0.15)',
      borderRadius: 12,
      padding: 4,
    }}>{children}</div>
  </div>
);

const Row = ({ k, v, mono }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(139,92,246,0.08)',
    fontSize: 13,
  }}>
    <span style={{ color: '#8885a0' }}>{k}</span>
    <span style={{ color: '#e0dff0', fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 12 : 13 }}>{v ?? '—'}</span>
  </div>
);

export default UserDetailDrawer;
